import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Check, MessageCircle, ChevronRight } from "lucide-react";
import { ProductCard } from "../components/site/ProductCard";
import { Reveal, spotMove } from "../components/site/Motion";
import { useRfq } from "../components/site/RfqContext";
import { QuoteConfigurator } from "../components/site/QuoteConfigurator";
import { NotFoundBlock } from "./Category";
import { api, fileUrl, useSettings, waLink } from "../lib/api";
import { Seo, crumbsLd, absUrl, SITE_URL } from "../components/site/Seo";

const clip = (s, n = 158) => (s.length > n ? `${s.slice(0, n - 1).replace(/\s+\S*$/, "")}…` : s);

const Viewer = ({ images, video, name }) => {
  const media = [...images.map((u) => ({ t: "img", u })), ...(video ? [{ t: "video", u: video }] : [])];
  const [i, setI] = useState(0);
  const cur = media[i] || media[0];
  return (
    <div>
      <div onMouseMove={spotMove} className="spot clip-corner relative aspect-square overflow-hidden bg-[#07090e]" data-testid="product-image-viewer">
        <div className="pointer-events-none absolute inset-0 z-[1]" style={{ background: "radial-gradient(60% 50% at 50% 35%, rgba(56,189,248,.18), transparent 70%)" }} />
        {cur?.t === "video" ? (
          <video src={fileUrl(cur.u)} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        ) : (
          <motion.img key={cur?.u} initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} src={fileUrl(cur?.u)} alt={name} className="h-full w-full object-cover" />
        )}
      </div>
      {media.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-3">
          {media.map((m, k) => (
            <button key={k} data-testid={`product-thumb-${k}`} onClick={() => setI(k)} className={`aspect-square overflow-hidden border-2 bg-[#07090e] ${k === i ? "border-[#0a6cff]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              {m.t === "video" ? <span className="font-mono-x flex h-full items-center justify-center text-[10px] text-sky-300">VIDEO</span> : <img src={fileUrl(m.u)} alt="" className="h-full w-full object-cover" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { open } = useRfq();
  const s = useSettings();
  const { data, isLoading, isError } = useQuery({ queryKey: ["product", slug], queryFn: () => api.get(`/products/${slug}`).then((r) => r.data) });
  if (isError) return <NotFoundBlock />;
  const p = data?.product;
  if (isLoading || !p) return <div className="min-h-screen bg-[#07090e]" data-testid="product-loading" />;
  const cat = data?.category || {};
  const related = Array.isArray(data?.related) ? data.related : [];
  const specs = Array.isArray(p.specs) ? p.specs : [];
  const apps = Array.isArray(p.applications) ? p.applications : [];
  const path = `/products/${cat.slug}/${p.slug}`;
  const productLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        name: p.name,
        description: p.description,
        image: (Array.isArray(p.images) ? p.images : []).map(absUrl).filter(Boolean),
        category: cat.name,
        url: SITE_URL ? `${SITE_URL}${path}` : undefined,
        brand: { "@type": "Brand", name: "Autocrat Rubber Industries" },
        manufacturer: { "@id": `${SITE_URL}/#org` },
        additionalProperty: specs.map((s) => ({ "@type": "PropertyValue", name: s?.label, value: s?.value })),
      },
      crumbsLd([["Home", "/"], ["Products", "/products"], [cat.name, `/products/${cat.slug}`], [p.name, path]]),
    ],
  };

  return (
    <div data-testid="product-detail-view">
      <Seo path={path} type="product" title={`${p.name} | ${cat.name} | Autocrat Rubber`} description={clip(`${p.description} Get a quote for ${p.name.toLowerCase()} from Autocrat Rubber.`)} image={p.images?.[0]} jsonLd={productLd} />
      <section className="noise relative bg-[#07090e] pb-20 pt-32 text-white md:pt-40">
        <div className="grid-lines absolute inset-0" />
        <div className="container-x relative">
          <nav className="font-mono-x mb-10 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-sky-400">Home</Link><ChevronRight className="h-3 w-3" />
            <Link to="/products" className="hover:text-sky-400">Products</Link><ChevronRight className="h-3 w-3" />
            <Link to={`/products/${cat.slug}`} className="hover:text-sky-400">{cat.name}</Link><ChevronRight className="h-3 w-3" />
            <span className="text-slate-200">{p.name}</span>
          </nav>
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6"><Viewer images={Array.isArray(p.images) ? p.images : []} video={p.video} name={p.name} /></Reveal>
            <div className="lg:col-span-6 lg:pl-6">
              <p className="eyebrow text-sky-400">{cat.name}</p>
              <h1 data-testid="product-title" className="font-display mt-4 text-5xl font-black uppercase leading-[0.88] sm:text-6xl lg:text-7xl">{p.name}</h1>
              <p className="mt-6 text-sm leading-relaxed text-slate-300 md:text-base">{p.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button data-testid="product-rfq-cta" onClick={() => open(p.name, p.id)} className="btn-electric">Request Quote <ArrowRight className="h-4 w-4" /></button>
                <a data-testid="product-whatsapp-cta" href={waLink(s.whatsapp, `Hello Autocrat Rubber, I need a quote for: ${p.name}`)} target="_blank" rel="noreferrer" className="btn-ghost-steel"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
              </div>
              {specs.length > 0 && (
                <div className="mt-12" data-testid="product-spec-table">
                  <p className="eyebrow text-slate-400">Specifications</p>
                  <dl className="mt-4 border-t border-slate-800">
                    {specs.map((sp, k) => (
                      <div key={k} className="grid grid-cols-5 gap-4 border-b border-slate-800 py-3.5 text-sm">
                        <dt className="font-mono-x col-span-2 text-xs uppercase tracking-wider text-slate-400">{sp?.label}</dt>
                        <dd className="col-span-3 text-slate-100">{sp?.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {apps.length > 0 && (
                <div className="mt-10" data-testid="product-applications">
                  <p className="eyebrow text-slate-400">Applications</p>
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {apps.map((a) => <li key={a} className="flex items-center gap-2 text-sm text-slate-200"><Check className="h-4 w-4 text-sky-400" />{a}</li>)}
                  </ul>
                </div>
              )}
              <QuoteConfigurator key={p.id} product={p} />
            </div>
          </div>
        </div>
      </section>
      {related.length > 0 && (
        <section className="bg-slate-50 py-20">
          <div className="container-x">
            <p className="eyebrow text-[#0a6cff]">More in {cat.name}</p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r, i) => <ProductCard key={r.id} p={r} i={i} catSlug={cat.slug} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
